import React from "react";
import { Link } from "react-router-dom";
import { HelpCircle, Mail, FileText, MessageCircle, BookOpen } from "lucide-react";
import { useDocMeta } from "../../lib/useDocMeta";

const FAQS = [
  {
    q: "How do I sign in?",
    a: "Enter your email at /customer/login — we email you a single-use magic link that expires in 15 minutes.",
  },
  {
    q: "Where are my reports?",
    a: "Dashboard → Reports. Reports are linked to your email account; reports you purchased before signing in are automatically associated when you log in.",
  },
  {
    q: "Can I still use share links I sent to teammates?",
    a: "Yes. Existing /report/share/:token links continue to work for anyone, with no sign-in required.",
  },
  {
    q: "Does the AI Copilot give legal or financial advice?",
    a: "No. Copilot explains scores, risks, and recommendations in the report you own. It cannot provide legal, tax, financial, or award-guarantee advice.",
  },
  {
    q: "What if my magic link expired?",
    a: "Just request a new one — links are single-use and expire after 15 minutes for security.",
  },
];

export default function CustomerHelp() {
  useDocMeta({ title: "Help · GovBidIQ", path: "/app/help" });
  return (
    <div className="max-w-3xl space-y-6" data-testid="cust-help">
      <header>
        <div className="tick-label text-sky-400 mb-1">/ Help</div>
        <h1 className="font-display font-black text-3xl text-slate-100 inline-flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-sky-400" /> Help & Support
        </h1>
        <p className="text-slate-400 text-sm mt-1">Customer platform basics, account access, and Copilot guidance.</p>
      </header>

      <section className="grid sm:grid-cols-3 gap-px bg-[var(--border-soft)]">
        <Card to="/app/reports" icon={FileText} label="View Reports" />
        <Card to="/app/copilot" icon={MessageCircle} label="Ask Copilot" />
        <Card to="/app/learning" icon={BookOpen} label="Learning Hub" />
      </section>

      <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
        <div className="tick-label text-sky-400 mb-3">/ Frequently Asked</div>
        <ul className="divide-y divide-[var(--border-soft)]">
          {FAQS.map((f, i) => (
            <li key={i} className="py-3" data-testid={`cust-help-faq-${i}`}>
              <div className="font-display font-bold text-slate-100 text-sm">{f.q}</div>
              <div className="text-slate-400 text-sm mt-1 leading-relaxed">{f.a}</div>
            </li>
          ))}
        </ul>
      </section>

      <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
        <div className="tick-label text-sky-400 mb-2">/ Still stuck?</div>
        <p className="text-sm text-slate-300 leading-relaxed">
          Email <a href="mailto:BidNoBidIQ@aol.com" className="text-sky-400 hover:text-sky-300">BidNoBidIQ@aol.com</a> with your account email and we&apos;ll respond within one business day.
        </p>
        <a href="mailto:BidNoBidIQ@aol.com" data-testid="cust-help-email" className="mt-3 inline-flex items-center gap-2 border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-widest uppercase px-3 py-2">
          <Mail className="w-3.5 h-3.5" /> BidNoBidIQ@aol.com
        </a>
      </section>
    </div>
  );
}

function Card({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="bg-[var(--bg-secondary)] p-4 hover:bg-slate-900/70 flex items-center gap-3">
      <Icon className="w-5 h-5 text-sky-400" strokeWidth={1.8} />
      <span className="font-mono text-[11px] tracking-widest uppercase text-slate-200">{label}</span>
    </Link>
  );
}
